import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { UserRole } from "@exam/shared";
import { useAuth } from "./AuthContext";

/**
 * Route guard. Use as a layout route:
 *   <Route element={<ProtectedRoute role="admin" />}> ... </Route>
 *
 * Anonymous users go to /login (with the attempted path preserved); a signed-in
 * user with the wrong role is bounced to their own home rather than shown a 403.
 */
export function ProtectedRoute({
  role,
  children,
}: {
  /** Omit to require any signed-in user. */
  role?: UserRole;
  children?: ReactNode;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoading />;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (role && user.role !== role) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

export function homePathFor(role: UserRole): string {
  return role === "admin" ? "/admin" : "/student";
}

function FullPageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-xs text-ink-faint">Loading…</p>
    </div>
  );
}
